import { useCallback, useEffect, useMemo, useState } from 'react';

import { type ClientListing, ListingStatus } from 'src/generated/graphql';
import { expireListing } from 'src/services/api';
import { ConsoleErrorId, consoleErrorWrapper } from 'src/utils';

type ClientListingSubset = Pick<ClientListing, 'id'>;

export type ClientListingExpireUpdate = Pick<
  ClientListing,
  'autoRenew' | 'creditRemaining' | 'expiryDate' | 'status'
>;

type UseExpireListingStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; listing: ClientListingExpireUpdate }
  | { kind: 'error'; message: string };

export const useExpireListing = (listing: ClientListingSubset) => {
  const [status, setStatus] = useState<UseExpireListingStatus>({
    kind: 'idle',
  });

  const expire = useCallback(
    async (reason: string, comments: string) => {
      setStatus({ kind: 'loading' });

      try {
        const responseListing = await expireListing(
          listing.id.toString(),
          reason,
          comments,
        );
        validateResponse(responseListing);

        const clientListing: ClientListingExpireUpdate = {
          autoRenew: responseListing.autoRenew,
          creditRemaining: responseListing.creditRemaining,
          expiryDate: responseListing.expiryDate,
          status: ListingStatus.Expired,
        };

        const resultStatus: UseExpireListingStatus = {
          kind: 'success',
          listing: clientListing,
        };

        setStatus(resultStatus);
        return resultStatus;
      } catch (error: any) {
        const resultStatus: UseExpireListingStatus = {
          kind: 'error',
          message:
            error?.errorMessage ?? error?.status ?? 'Something went wrong',
        };
        setStatus(resultStatus);
        return resultStatus;
      }
    },
    [listing.id],
  );

  useEffect(() => {
    // Reset this hook's status state if the underlying listing is updated. We want to do
    // this so that if the underlying listing is refreshed (e.g. via a "refetchQueries"
    // call) then we allow the user to act on this listing again. If we don't do
    // this then the local state will remain stale which may make subsequent attempts
    // to expire fail (no-op)
    if (status.kind === 'success' || status.kind === 'error') {
      setStatus({ kind: 'idle' });
    }
  }, [status.kind, listing]);

  return useMemo(() => ({ status, expire }), [status, expire]);
};

// Log so we know but don't do anything else... we don't want to interrupt current handling
const validateResponse = (response: any) => {
  const isValid =
    typeof response.autoRenew === 'boolean' &&
    typeof response.creditRemaining === 'number' &&
    typeof response.expiryDate === 'string';

  if (!isValid) {
    consoleErrorWrapper(
      ConsoleErrorId.ExpireListingValidation,
      `Expire listing response payload is not correct. Payload: ${JSON.stringify(
        response,
      )}`,
    );
  }
};
